/**
 * React hooks wrapping the userBusyService.
 *
 * Use these in components.  The plain-JS API in
 * /utils/userBusyService is for non-React callers (e.g. setInterval
 * callbacks).
 */
import { useEffect, useState } from 'react';
import {
  markBusy,
  markIdle,
  isUserBusy,
  subscribeToBusyState,
} from '../utils/userBusyService';

/**
 * Read-only hook: returns the current "user busy" boolean and
 * re-renders the consumer when it flips.
 *
 *   const busy = useIsUserBusy();
 */
export function useIsUserBusy() {
  const [busy, setBusy] = useState(() => isUserBusy());
  useEffect(() => {
    const unsub = subscribeToBusyState(setBusy);
    return unsub;
  }, []);
  return busy;
}

/**
 * Declarative marker: as long as ``isBusy`` is true, the service is
 * marked busy with ``reason``.  When it flips to false (or the
 * component unmounts) it's marked idle.  Safe to mount many of these
 * with different reasons — they reference-count.
 *
 *   useMarkBusyWhile('chart-load', clientDetailLoading);
 *   useMarkBusyWhile('modal:claim-edit', showClaimEditDialog);
 */
export function useMarkBusyWhile(reason, isBusy) {
  useEffect(() => {
    if (!reason) return undefined;
    if (!isBusy) return undefined;
    markBusy(reason);
    return () => markIdle(reason);
  }, [reason, isBusy]);
}
